/**
 * วิชาตัวเบาจากดันเจี้ยนและบอส (docs/archive/QINGGONG_DROPS_RED_LANTERN.md Part A, data/qinggongDrops.ts).
 *
 * Listens for the chests and kills that drop pages (ถ้ำคลื่นคำราม: the room-3 boss and the chest; the world bosses: the
 * `worldBossLoot` payout), pays them as goods to the player (personal loot: each paid player rolls their own), binds
 * pages into the manual when a set is complete, spends the manual (and the seals) when the art is learned, and keeps a
 * learned dropped art from being trained past `selfCap` without its teacher in reach. A bad-luck counter per source
 * makes the third dry claim pay (the fifth gold chest for a seal). Draws only from the World's Rng.
 */
import {
  QG_ARTS,
  QG_BOSS_ART,
  QG_DUNGEON,
  QG_SEAL_BOSSES,
  QG_WB,
  pityKey,
  qgManualId,
  qgPageId,
  type QgDropKind,
  qgSealId,
} from '../data/qinggongDrops';
import { getQinggong } from '../data/qinggong';
import { qinggongTrainBlock } from '../qinggong';
import type { PlayerCommand, WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

const BIND_EVERY = 1;

/** The player was paid a page, a manual (bound or whole) or a seal; `art` is the art (the seal's: its boss). Text is in the notice. */
export type QgDropEvent = { type: 'qgDrop'; kind: QgDropKind; art: string; n: number; why: string };

export class QinggongDropsSystem implements WorldSystem {
  readonly name = 'qinggongDrops';
  private t = 0;

  constructor(private readonly h: SystemHost) {}

  private get pr() {
    return this.h.world.progress;
  }

  private goods(): Record<string, number> {
    return this.pr.trades.goods;
  }

  private add(id: string, n = 1): void {
    const g = this.goods();
    g[id] = (g[id] ?? 0) + n;
  }

  private take(id: string, n = 1): void {
    const g = this.goods();
    const left = (g[id] ?? 0) - n;
    if (left > 0) g[id] = left;
    else delete g[id];
  }

  /** The bad-luck counters (`progress.qgPity`; absent until a roll). */
  private pity(): Record<string, number> {
    return (this.pr.qgPity ??= {});
  }

  /**
   * One guarded roll: pays on a hit, or when `guard - 1` earlier claims were dry. Always draws once, so the stream stays
   * the same whichever way it goes.
   */
  private roll(key: string, chance: number, guard: number): boolean {
    const p = this.pity();
    const hit = this.h.random() < chance || (p[key] ?? 0) >= guard - 1;
    if (hit) delete p[key];
    else p[key] = (p[key] ?? 0) + 1;
    return hit;
  }

  private page(art: string, why: string): void {
    this.add(qgPageId(art));
    this.h.notice(`ได้รับหน้าตำรา${QG_ARTS[art]!.name} (${why})`);
    this.h.emit({ type: 'qgDrop', kind: 'page', art, n: this.goods()[qgPageId(art)] ?? 1, why });
    this.bind(art);
  }

  private manual(art: string, why: string): void {
    this.add(qgManualId(art));
    this.h.notice(`ได้รับ${QG_ARTS[art]!.manualName} (${why})`);
    this.h.emit({ type: 'qgDrop', kind: 'manual', art, n: 1, why });
  }

  /** A full set of pages is sewn into the manual (not for an art already learned or a manual already held). */
  private bind(art: string): void {
    const a = QG_ARTS[art]!;
    const g = this.goods();
    if (this.pr.qinggong[art] || (g[qgManualId(art)] ?? 0) > 0 || (g[qgPageId(art)] ?? 0) < a.pages) return;
    this.take(qgPageId(art), a.pages);
    this.add(qgManualId(art));
    this.h.notice(`เย็บหน้าตำราครบ ${a.pages} หน้าเป็น${a.manualName}แล้ว`);
    this.h.emit({ type: 'qgDrop', kind: 'bound', art, n: 1, why: 'เย็บหน้าตำรา' });
  }

  step(dt: number): void {
    this.t += dt;
    if (this.t < BIND_EVERY) return;
    this.t = 0;
    // Pages that came by trade bind too.
    for (const art of Object.keys(QG_ARTS)) this.bind(art);
  }

  refuse(cmd: PlayerCommand): boolean {
    if (cmd.t !== 'qinggongTrain') return false;
    const block = qinggongTrainBlock(this.pr, cmd.id, this.h.world.map.zone);
    if (!block?.startsWith('ขั้นสูงต้องให้ครู')) return false;
    this.h.notice(block);
    return true;
  }

  observe(ev: WorldEvent): void {
    const w = this.h.world;
    switch (ev.type) {
      case 'qinggong': {
        if (ev.level !== 1) return;
        const L = getQinggong(ev.id).learn;
        if (L.manual && (this.goods()[L.manual] ?? 0) > 0) this.take(L.manual);
        for (const s of L.seals ?? []) if ((this.goods()[s] ?? 0) > 0) this.take(s);
        return;
      }
      case 'dungeonClear': {
        if (!ev.rewarded) return;
        const art = QG_DUNGEON.art;
        if (this.h.random() < QG_DUNGEON.chest.manual) this.manual(art, 'หีบถ้ำคลื่นคำราม');
        else if (this.roll(pityKey.dungeon(art), QG_DUNGEON.chest.page, QG_DUNGEON.guard)) this.page(art, 'หีบถ้ำคลื่นคำราม');
        return;
      }
      case 'death': {
        const e = w.entities.get(ev.id);
        if (e?.brain?.defId !== QG_DUNGEON.roomBoss.monster || w.player.dead) return;
        if (this.h.random() < QG_DUNGEON.roomBoss.page) this.page(QG_DUNGEON.art, 'เจ้าคลื่นเงิน');
        return;
      }
      case 'worldBossLoot': {
        if (ev.tier === 'none') return;
        const art = QG_BOSS_ART[ev.id];
        if (!ev.full) {
          // After the day's full chests a kill still tries for a page, with no guard.
          if (this.h.random() < QG_WB.late) this.page(art, 'บอสโลก');
          return;
        }
        if (ev.tier === 'gold' && this.h.random() < QG_WB.manual.gold) this.manual(art, 'หีบทอง');
        else if (this.roll(pityKey.boss(ev.id), QG_WB.page[ev.tier], QG_WB.guard)) this.page(art, 'บอสโลก');
        if (ev.tier === 'gold') this.rollSeal(ev.id);
        return;
      }
    }
  }

  /** A gold full chest may carry the boss's ตราเมฆา; the fifth gold chest without it pays it. */
  private rollSeal(boss: (typeof QG_SEAL_BOSSES)[number]): void {
    const seal = qgSealId(boss);
    if ((this.goods()[seal] ?? 0) > 0 || this.pr.qinggong['cloud-ascent']) return;
    if (this.roll(pityKey.seal(boss), QG_WB.seal.chance, QG_WB.seal.guard)) {
      this.add(seal);
      this.h.notice('ได้รับตราเมฆา');
      this.h.emit({ type: 'qgDrop', kind: 'seal', art: boss, n: 1, why: 'บอสโลก' });
    }
  }
}
